import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CreditCard, Plus, Package, FileText, ExternalLink, AlertCircle, CheckCircle2, Settings as SettingsIcon, BarChart3, ShieldCheck, ShieldAlert } from "lucide-react";

// Lazy so the recharts dependency only loads when the user actually opens
// the Reports tab. Without this, every visit to /payments paid for the
// chart library even though most sessions never look at the report tab.
const ReportsPage = lazy(() => import("./ReportsPage").then((m) => ({ default: m.ReportsPage })));
import { Avatar, Badge, Button, EmptyState, Input, Label, Modal, ModalFooter, Paginator, Select, Skeleton, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { formatMoney, formatRelative, formatDateTime } from "@/lib/format";
import { SmartListPicker } from "@/features/lists/SmartListPicker";
import { useSmartList, smartListQueryParams } from "@/features/lists/SmartListContext";
import type { Invoice, Payment, PaymentProviderConfig, Product } from "@/lib/types";
import { cn } from "@/lib/utils";

type Tab = "payments" | "invoices" | "reports" | "products" | "settings";
const TABS: { k: Tab; l: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { k: "payments", l: "Payments",  Icon: CreditCard },
  { k: "invoices", l: "Invoices",  Icon: FileText },
  { k: "reports",  l: "Reports",   Icon: BarChart3 },
  { k: "products", l: "Products",  Icon: Package },
  { k: "settings", l: "Provider",  Icon: SettingsIcon },
];

export function PaymentsPage() {
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const tab: Tab = (TABS.some((t) => t.k === tabParam) ? tabParam : "payments") as Tab;
  const [config, setConfig] = useState<PaymentProviderConfig | null>(null);

  useEffect(() => {
    api.get<{ config: PaymentProviderConfig | null }>("/payments/config")
      .then((d) => setConfig(d.config))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Payments</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Charge customers, manage products, and track invoices.
            {!config?.enabled && <span className="ml-2 text-amber-300">Connect a provider to start collecting payments.</span>}
          </p>
        </div>
        {config?.enabled && (
          <Badge color={config.mode === "live" ? "emerald" : "amber"}>
            {config.provider} · {config.mode === "live" ? "Live mode" : "Test mode"}
          </Badge>
        )}
      </div>

      {/* Mobile-friendly: chips evenly distributed (flex-1) on phones, label
          + icon at sm+. URL-driven so /payments/<tab> deep-links. */}
      <div className="flex gap-1 border-b border-zinc-800">
        {TABS.map(({ k, l, Icon }) => (
          <button
            key={k}
            type="button"
            onClick={() => navigate(k === "payments" ? "/payments" : `/payments/${k}`)}
            title={l}
            className={cn(
              "inline-flex flex-1 sm:flex-initial items-center justify-center gap-2 border-b-2 px-2 sm:px-3 py-2 text-sm font-medium transition-colors",
              tab === k ? "border-rose-500 text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-300"
            )}
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{l}</span>
          </button>
        ))}
      </div>

      {tab === "payments" && <PaymentsTab />}
      {tab === "invoices" && <InvoicesTab />}
      {tab === "reports"  && <Suspense fallback={<Skeleton className="h-96" />}><ReportsPage /></Suspense>}
      {tab === "products" && <ProductsTab />}
      {tab === "settings" && <ProviderSettingsTab onChanged={(c) => setConfig(c)} />}
    </div>
  );
}

// ─── Invoices tab ──────────────────────────────────────────────────────────
const INVOICES_PAGE_SIZE = 50;

function InvoicesTab() {
  const toast = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const { listId, filter: smartFilter, set: setSmartList } = useSmartList();

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ invoices: Invoice[]; total: number }>(
        "/payments/invoices",
        { ...smartListQueryParams(listId, smartFilter), limit: INVOICES_PAGE_SIZE, offset }
      );
      setInvoices(d.invoices);
      setTotal(d.total ?? d.invoices.length);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setLoading(false); }
  };
  // Reset to page 0 when the filter changes — otherwise the user could
  // land on offset 200 of a freshly-narrowed result set that has 12 rows
  // and see an empty page with no obvious way back.
  useEffect(() => { setOffset(0); }, [listId, smartFilter]);
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [listId, smartFilter, offset]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <SmartListPicker
          listId={listId}
          filter={smartFilter}
          onChange={(lid, f) => setSmartList(lid, f)}
        />
      </div>
      {loading ? <div className="space-y-2">{Array.from({length:5}).map((_,i)=><Skeleton key={i} className="h-16"/>)}</div>
       : invoices.length === 0
       ? <EmptyState icon={FileText} title="No invoices match" description="Invoices you create on a contact's page will show up here." />
       : <InvoicesTable invoices={invoices} />}
      <Paginator total={total} limit={INVOICES_PAGE_SIZE} offset={offset} onChange={setOffset} />
    </div>
  );
}

function InvoicesTable({ invoices }: { invoices: Invoice[] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-800/70 bg-zinc-900/40 text-xs uppercase tracking-wider text-zinc-500">
            <th className="px-3 py-2.5 text-left font-medium">Status</th>
            <th className="px-3 py-2.5 text-left font-medium">Contact</th>
            <th className="px-3 py-2.5 text-left font-medium">Amount</th>
            <th className="px-3 py-2.5 text-left font-medium">Created</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => (
            <tr key={inv.id} className="group border-b border-zinc-800/40 hover:bg-zinc-900/30">
              <td className="px-3 py-2.5"><InvoiceStatus status={inv.status} /></td>
              <td className="px-3 py-2.5">
                {inv.contact_id ? (
                  <Link to={`/contacts/${inv.contact_id}`} className="text-zinc-200 hover:text-rose-300">{inv.contact_name ?? "Unknown"}</Link>
                ) : <span className="text-zinc-500">—</span>}
              </td>
              <td className="px-3 py-2.5 font-medium text-zinc-200 tabular-nums">{formatMoney(inv.total_cents, inv.currency)}</td>
              <td className="px-3 py-2.5 text-zinc-500">{formatRelative(inv.created_at)}</td>
              <td className="px-3 py-2.5 text-right">
                {inv.hosted_url && (
                  <a href={inv.hosted_url} target="_blank" rel="noreferrer" className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200" aria-label="Open hosted invoice">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceStatus({ status }: { status: Invoice["status"] }) {
  const map: Record<Invoice["status"], { color: string; label: string }> = {
    draft:          { color: "zinc",    label: "Draft" },
    open:           { color: "indigo",  label: "Open" },
    paid:           { color: "emerald", label: "Paid" },
    uncollectible:  { color: "rose",    label: "Uncollectible" },
    void:           { color: "zinc",    label: "Void" },
  };
  const v = map[status];
  return <Badge color={v.color}>{v.label}</Badge>;
}

// ─── Payments tab ─────────────────────────────────────────────────────────
const PAYMENTS_PAGE_SIZE = 50;

function PaymentsTab() {
  const toast = useToast();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get<{ payments: Payment[]; total: number }>("/payments", { limit: PAYMENTS_PAGE_SIZE, offset })
      .then((d) => { setPayments(d.payments); setTotal(d.total ?? d.payments.length); })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed"))
      .finally(() => setLoading(false));
  }, [toast, offset]);

  if (loading) return <div className="space-y-2">{Array.from({length:5}).map((_,i)=><Skeleton key={i} className="h-14"/>)}</div>;
  if (payments.length === 0) {
    return <EmptyState icon={CreditCard} title="No payments yet" description="When customers pay an invoice, the payment will appear here." />;
  }
  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800/70 bg-zinc-900/40 text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-3 py-2.5 text-left font-medium">Status</th>
              <th className="px-3 py-2.5 text-left font-medium">Contact</th>
              <th className="px-3 py-2.5 text-left font-medium">Amount</th>
              <th className="px-3 py-2.5 text-left font-medium">Method</th>
              <th className="px-3 py-2.5 text-left font-medium">When</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-zinc-800/40 hover:bg-zinc-900/30">
                <td className="px-3 py-2.5">
                  {p.status === "succeeded" && <Badge color="emerald" size="sm"><CheckCircle2 className="h-3 w-3 mr-1" />Succeeded</Badge>}
                  {p.status === "failed"    && <Badge color="rose" size="sm"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge>}
                  {p.status === "pending"   && <Badge color="amber" size="sm">Pending</Badge>}
                  {p.status === "refunded"  && <Badge color="zinc" size="sm">Refunded</Badge>}
                </td>
                <td className="px-3 py-2.5">{p.contact_id ? <Link to={`/contacts/${p.contact_id}`} className="text-zinc-200 hover:text-rose-300">{p.contact_name}</Link> : "—"}</td>
                <td className="px-3 py-2.5 font-medium text-zinc-200 tabular-nums">{formatMoney(p.amount_cents, p.currency)}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-500">
                  {p.kind === "manual" ? "Manual" : (p.payment_method.brand && p.payment_method.last4) ? `${p.payment_method.brand} •••• ${p.payment_method.last4}` : p.provider}
                </td>
                <td className="px-3 py-2.5 text-zinc-500">{formatRelative(p.charged_at ?? p.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Paginator total={total} limit={PAYMENTS_PAGE_SIZE} offset={offset} onChange={setOffset} />
    </div>
  );
}

// ─── Products tab ─────────────────────────────────────────────────────────
function ProductsTab() {
  const toast = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ products: Product[] }>("/payments/products");
      setProducts(d.products);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-500">{products.length} product{products.length === 1 ? "" : "s"}</p>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>New product</Button>
      </div>
      {loading ? (
        <div className="space-y-2">{Array.from({length:4}).map((_,i)=><Skeleton key={i} className="h-16"/>)}</div>
      ) : products.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No products yet"
          description="Products are reusable line items you can add to invoices or sell as subscriptions."
          action={<Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>Create your first product</Button>}
        />
      ) : (
        <ul className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          {products.map((p) => (
            <li key={p.id} className="flex items-center justify-between border-b border-zinc-800/40 px-4 py-3 last:border-0">
              <div>
                <p className="text-sm font-medium text-zinc-100">{p.name}</p>
                <p className="text-xs text-zinc-500">
                  {formatMoney(p.amount_cents, p.currency)}
                  {p.pricing_type === "subscription" && ` / ${p.recurring_interval_count ?? 1} ${p.recurring_interval}`}
                  {p.description && ` · ${p.description}`}
                </p>
              </div>
              <Badge color={p.pricing_type === "subscription" ? "violet" : "zinc"} size="sm">
                {p.pricing_type === "subscription" ? "Subscription" : "One-time"}
              </Badge>
            </li>
          ))}
        </ul>
      )}
      <ProductCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />
    </div>
  );
}

function ProductCreateDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [pricingType, setPricingType] = useState<"one_time" | "subscription">("one_time");
  const [interval, setInterval] = useState<"month" | "year" | "week" | "day">("month");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim() || !amount) return;
    setSaving(true);
    try {
      await api.post("/payments/products", {
        name: name.trim(),
        description: description.trim() || null,
        pricing_type: pricingType,
        amount_cents: Math.round(parseFloat(amount) * 100),
        currency,
        recurring_interval: pricingType === "subscription" ? interval : null,
        recurring_interval_count: 1,
      });
      toast.success("Product created");
      setName(""); setDescription(""); setAmount(""); setCurrency("USD"); setPricingType("one_time");
      onCreated();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="New product" size="md">
      <div className="space-y-3">
        <div><Label required>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Pro Plan" autoFocus /></div>
        <div><Label>Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label required>Price</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="99.00" /></div>
          <div>
            <Label>Currency</Label>
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {["USD","EUR","GBP","CAD","AUD","JPY"].map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
        </div>
        <div>
          <Label>Type</Label>
          <Select value={pricingType} onChange={(e) => setPricingType(e.target.value as typeof pricingType)}>
            <option value="one_time">One-time</option>
            <option value="subscription">Subscription</option>
          </Select>
        </div>
        {pricingType === "subscription" && (
          <div>
            <Label>Bills every</Label>
            <Select value={interval} onChange={(e) => setInterval(e.target.value as typeof interval)}>
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </Select>
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!name.trim() || !amount}>Create</Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Provider settings tab ────────────────────────────────────────────────
/**
 * Stripe (and any other payment provider) is connected at the SyncSuite
 * account level via the Connections UI, NOT here. The credentials live in
 * the platform connection store and are injected for billable Stripe
 * service calls. This page just shows status + non-credential preferences
 * (mode, default currency, on/off) and links to the upstream Connections
 * page when the user wants to (re)connect.
 */
function ProviderSettingsTab({ onChanged }: { onChanged: (c: PaymentProviderConfig) => void }) {
  const toast = useToast();
  const [config, setConfig] = useState<PaymentProviderConfig | null>(null);
  const [providers, setProviders] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const d = await api.get<{ config: PaymentProviderConfig | null; supported_providers: string[] }>("/payments/config");
      setConfig(d.config);
      setProviders(d.supported_providers);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };
  useEffect(() => { void load(); }, []);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      // `enabled` mirrors connection state — if the user has connected the
      // provider in their SyncSuite dashboard we treat it as on, otherwise
      // off. Surfacing this as a separate checkbox in the UI was confusing.
      const connectedNow = Object.values(config.config_mask).some(Boolean);
      await api.patch("/payments/config", {
        provider: config.provider,
        mode: config.mode,
        enabled: connectedNow,
        default_currency: config.default_currency,
      });
      toast.success("Saved");
      await load();
      if (config) onChanged({ ...config, enabled: connectedNow });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post<{ ok: boolean; error?: string }>("/payments/test-connection");
      if (r.ok) toast.success("Connection OK");
      else toast.error(r.error ?? "Connection failed");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setTesting(false); }
  };

  if (!config) return <Skeleton className="h-64" />;

  // We treat any field on `config_mask` as "credentials present in the
  // upstream connection." If none are set, the user hasn't connected yet
  // at the SyncSuite Connections layer.
  const connected = Object.values(config.config_mask).some(Boolean);
  const providerName = config.provider.charAt(0).toUpperCase() + config.provider.slice(1);

  return (
    <div className="lg:max-w-2xl space-y-4">
      <p className="text-sm text-zinc-400">
        Choose how you want to charge customers. Currently we support {providers.join(", ")}.
        To swap providers later, the AI just edits the active provider here — your invoices, products, and customers stay in place.
      </p>

      {/* Connection status — sourced from the SyncSuite Connections page,
          not entered here. */}
      <div className={cn(
        "flex items-start gap-3 rounded-xl border p-4",
        connected ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5",
      )}>
        <div className={cn(
          "flex h-9 w-9 items-center justify-center rounded-lg shrink-0",
          connected ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300",
        )}>
          {connected ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-zinc-100">{providerName} connection</p>
            <Badge color={connected ? "emerald" : "amber"} size="xs">
              {connected ? "Connected via SyncSuite" : "Not connected"}
            </Badge>
          </div>
          <p className="mt-1 text-xs text-zinc-400">
            {connected
              ? `Your ${providerName} credentials live in your SyncSuite account's Connections, not in the CRM. To rotate keys, manage webhooks, or disconnect, open SyncSuite Connections in your dashboard.`
              : `To start collecting payments, open SyncSuite Connections in your dashboard and connect ${providerName}. The CRM will pick up the credentials automatically — no copy-pasting keys here.`}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-zinc-200">Preferences</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label>Provider</Label>
            <Select value={config.provider} onChange={(e) => setConfig({ ...config, provider: e.target.value })}>
              {providers.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </Select>
          </div>
          <div>
            <Label>Mode</Label>
            <Select value={config.mode} onChange={(e) => setConfig({ ...config, mode: e.target.value as "test" | "live" })}>
              <option value="test">Test mode</option>
              <option value="live">Live mode</option>
            </Select>
          </div>
        </div>
        <div>
          <Label>Default currency</Label>
          <Select value={config.default_currency} onChange={(e) => setConfig({ ...config, default_currency: e.target.value })}>
            {["USD","EUR","GBP","CAD","AUD","JPY"].map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={test} loading={testing} disabled={!connected}>Test connection</Button>
        <Button onClick={save} loading={saving}>Save</Button>
      </div>
    </div>
  );
}
